// @djsem/analysis-contract — AI Creative Prompt Builder & Versioning (Prompt 22C).
//
// Compiles project-scope creative CONTEXT (music, set, release, brand, lyrics, stems, scenes,
// user brief) into typed prompt bundles that AI providers can execute — WITHOUT owning any
// provider secrets, keys, or runtime execution (rule: no browser-visible provider keys).
// Rights/safety filtering happens HERE, before any bundle reaches a provider. Deterministic
// hashing for versioning; provider serialization is a pure map.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult, fnv1a } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";
import { stableStringify } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** The intended use case a creative AI prompt bundle is being built for. */
export type DJSEMAIPromptUseCase =
  | "ai_artwork" | "ai_video" | "social_copy"
  | "campaign_key_visual" | "visual_scene_concept"
  | "brand_world_variant" | "typography_motion";

/** Compiled creative context (track/set/release/brand/lyrics/stems/scenes/brief) used to build a prompt bundle. */
export interface DJSEMAIPromptContext {
  id: string;
  projectId: string;
  useCase: DJSEMAIPromptUseCase;
  trackContext?: {
    trackId: string; title?: string; artist?: string;
    bpm?: number; key?: string; moods: string[]; semanticTags: string[]; warnings: string[];
  };
  setContext?: {
    setId: string; title?: string; energyArcSummary?: string;
    emotionalArcSummary?: string; warnings: string[];
  };
  releaseContext?: {
    releaseId: string; title?: string; releaseType?: string;
    campaignTone?: string[]; warnings: string[];
  };
  brandWorldContext?: {
    brandWorldId: string; colors: string[]; typographySummary?: string;
    motifs: string[]; textures: string[]; motionLanguage: string[];
    visualTone: string[]; usageRules: string[]; restrictedElements: string[];
    warnings: string[];
  };
  lyricContext?: {
    allowedMode: "none" | "key_phrases_only" | "approved_lines" | "full_private_only";
    keyPhrases: string[]; warnings: string[];
  };
  stemContext?: { availableStemRoles: string[]; energyDrivers: string[]; warnings: string[]; };
  sceneContext?: { sceneIds: string[]; sceneIntentSummary: string[]; warnings: string[]; };
  userBrief?: string;
  warnings: string[];
}

/** A versioned, provider-ready prompt bundle compiled from a prompt context. */
export interface DJSEMAIPromptBundle {
  id: string;
  schemaVersion: string;
  projectId: string;
  useCase: DJSEMAIPromptUseCase;
  promptVersion: number;
  contextId: string;
  positivePrompt: string;
  negativePrompt: string;
  styleRules: string[];
  forbiddenElements: string[];
  providerHints: Record<string, unknown>;
  safetyWarnings: string[];
  rightsWarnings: string[];
  createdAt: string;
  createdBy?: string;
  sourceRefs: Array<{ type: string; id: string }>;
}

/** A recorded revision of a prompt bundle, capturing the diff and reason for the change. */
export interface DJSEMAIPromptRevision {
  id: string;
  promptBundleId: string;
  revisionNumber: number;
  changedAt: string;
  changedBy?: string;
  reason: string;
  diffSummary: string[];
  bundle: DJSEMAIPromptBundle;
}

/** The provider-specific payload derived from a prompt bundle, with secret-like keys redacted. */
export interface DJSEMAIProviderPromptPayload {
  bundleId: string;
  provider: string;
  positivePrompt: string;
  negativePrompt: string;
  parameters: Record<string, unknown>;
  redactions: string[];
}

/** Result of diffing two prompt bundle versions' forbidden elements. */
export interface DJSEMPromptDiffResult { added: string[]; removed: string[]; changed: string[]; }
/** An audit-trail entry recording an action taken against a prompt bundle. */
export interface DJSEMPromptAuditRecord { id: string; bundleId: string; recordedAt: string; recordedBy?: string; action: string; details: unknown; }

/** Current schema version stamped onto every created prompt bundle. */
export const AI_PROMPT_SCHEMA_VERSION = "1.0";

// ---- inputs -----------------------------------------------------------------

/** Input for {@link AICreativePromptContextService.buildContext}. */
export interface BuildPromptContextInput {
  id?: string;
  projectId: string;
  useCase: DJSEMAIPromptUseCase;
  track?: DJSEMAIPromptContext["trackContext"];
  set?: DJSEMAIPromptContext["setContext"];
  release?: DJSEMAIPromptContext["releaseContext"];
  brandWorld?: DJSEMAIPromptContext["brandWorldContext"];
  lyrics?: DJSEMAIPromptContext["lyricContext"];
  stems?: DJSEMAIPromptContext["stemContext"];
  scenes?: DJSEMAIPromptContext["sceneContext"];
  userBrief?: string;
}

/** Input for {@link AICreativePromptContextService.filterRestrictedContext}. */
export interface FilterPromptContextInput {
  context: DJSEMAIPromptContext;
  /** Rights state — export/preview/private. Restricted state cannot go to a public provider. */
  rightsState?: "unknown" | "cleared" | "restricted";
  /** True when the eventual generation is for public/commercial use. */
  isForPublicUse?: boolean;
}

/** Input for {@link AICreativePromptBuilderService.createPromptBundle}. */
export interface CreatePromptBundleInput {
  id?: string;
  context: DJSEMAIPromptContext;
  userBrief?: string;
  createdBy?: string;
  createdAt?: string;
}

/** Input for {@link AICreativePromptBuilderService.revisePromptBundle}. */
export interface RevisePromptBundleInput {
  existing: DJSEMAIPromptBundle;
  patch: Partial<Pick<DJSEMAIPromptBundle, "positivePrompt" | "negativePrompt" | "styleRules" | "forbiddenElements" | "providerHints">>;
  reason: string;
  changedBy?: string;
  changedAt?: string;
}

/** Input for {@link AICreativePromptBuilderService.createProviderPayload}. */
export interface CreateProviderPromptPayloadInput {
  bundle: DJSEMAIPromptBundle;
  provider: string;
  /** Optional provider-specific parameter map (dimensions, seed, aspect etc). Never keys/secrets. */
  parameters?: Record<string, unknown>;
}

/** Input for {@link AICreativePromptAuditService.diffPromptBundles}. */
export interface DiffPromptBundlesInput { a: DJSEMAIPromptBundle; b: DJSEMAIPromptBundle; }
/** Input for {@link AICreativePromptAuditService.createAuditRecord}. */
export interface CreatePromptAuditInput { bundleId: string; action: string; details: unknown; recordedAt?: string; recordedBy?: string; }

// ---- helpers ----------------------------------------------------------------

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// Local FNV-1a — shared/prep already exports stableStringify.

// Fields whose presence in providerHints indicates an attempt to smuggle secrets. This is
// belt-and-suspenders: the shape is `Record<string, unknown>` and the compiler can't stop it.
const FORBIDDEN_KEYS = new Set(["apiKey", "api_key", "authorization", "auth", "token", "secret", "password", "clientSecret", "privateKey", "sessionToken", "cookie"]);
// Words the negative prompt should always include (safety floor).
const DEFAULT_FORBIDDEN = ["nsfw", "graphic violence", "gore", "real-person deepfake", "real-brand-logo-copy"];

function collectWarnings(ctx: DJSEMAIPromptContext): { safety: string[]; rights: string[] } {
  const safety = new Set<string>();
  const rights = new Set<string>();
  const push = (arr: string[] | undefined) => (arr ?? []).forEach((w) => (/right|licen|clear|restrict|copyright|attribut/i.test(w) ? rights : safety).add(w));
  push(ctx.trackContext?.warnings); push(ctx.setContext?.warnings); push(ctx.releaseContext?.warnings);
  push(ctx.brandWorldContext?.warnings); push(ctx.lyricContext?.warnings); push(ctx.stemContext?.warnings); push(ctx.sceneContext?.warnings);
  push(ctx.warnings);
  return { safety: [...safety], rights: [...rights] };
}

// ---- services ---------------------------------------------------------------

/** Builds and rights/safety-filters creative prompt contexts before they reach a prompt builder. */
export class AICreativePromptContextService {
  /**
   * Assemble a prompt context from optional track/set/release/brand/lyric/stem/scene inputs.
   * @param input - the project/use-case and optional context slices.
   * @returns the constructed context.
   */
  buildContext(input: BuildPromptContextInput): DJSEMAIPromptContext {
    return {
      id: input.id ?? `ctx_${input.projectId}_${_seq.next()}`,
      projectId: input.projectId, useCase: input.useCase,
      trackContext: input.track, setContext: input.set, releaseContext: input.release,
      brandWorldContext: input.brandWorld, lyricContext: input.lyrics,
      stemContext: input.stems, sceneContext: input.scenes,
      userBrief: input.userBrief, warnings: [],
    };
  }

  /**
   * Strip rights-restricted material from a prompt context before it can reach a provider.
   * Restricted rights are removed (not just warned); unknown rights warn. Full lyrics never
   * leave private-only mode for public-use generations.
   * @param input - the context, its rights state, and whether it's for public use.
   * @returns a filtered copy of the context (never mutates the input).
   */
  filterRestrictedContext(input: FilterPromptContextInput): DJSEMAIPromptContext {
    const c: DJSEMAIPromptContext = JSON.parse(JSON.stringify(input.context));
    const warnings = new Set(c.warnings ?? []);

    if (input.rightsState === "restricted") {
      // Strip anything that could leak restricted material to a provider.
      if (c.lyricContext) { c.lyricContext.keyPhrases = []; c.lyricContext.allowedMode = "none"; }
      if (c.brandWorldContext) c.brandWorldContext.restrictedElements = [];
      warnings.add("BLOCK: rights restricted — restricted assets removed from prompt context.");
    }
    if (input.rightsState === "unknown") warnings.add("Rights state unknown — provider generation is at your risk.");
    if (input.isForPublicUse && c.lyricContext?.allowedMode === "full_private_only") {
      c.lyricContext.keyPhrases = [];
      c.lyricContext.allowedMode = "key_phrases_only";
      warnings.add("Full lyrics not permitted for public use — reduced to key_phrases_only.");
    }
    // BrandWorld restrictedElements never make it into prompt vocabulary.
    if (c.brandWorldContext?.restrictedElements?.length) {
      warnings.add(`Brand World restricted elements excluded from prompt (${c.brandWorldContext.restrictedElements.length}).`);
    }
    return { ...c, warnings: [...warnings] };
  }

  /**
   * Validate a prompt context's required fields.
   * @param ctx - the context to validate.
   * @returns validation result.
   */
  validateContext(ctx: DJSEMAIPromptContext): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!ctx.projectId) issues.push({ path: "$.projectId", message: "projectId required" });
    if (!ctx.useCase) issues.push({ path: "$.useCase", message: "useCase required" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "ai_prompt_context");
  }
}

/** Compiles a prompt context into a versioned, provider-ready prompt bundle. */
export class AICreativePromptBuilderService {
  /**
   * Build a new prompt bundle (v1) from a context, assembling positive/negative prompts, style rules, and source refs.
   * @param input - the context, optional override brief, and creation metadata.
   * @returns the constructed prompt bundle.
   */
  createPromptBundle(input: CreatePromptBundleInput): DJSEMAIPromptBundle {
    const c = input.context;
    const { safety, rights } = collectWarnings(c);

    // Positive prompt: assembled from context in a stable, human-readable order.
    const parts: string[] = [];
    if (input.userBrief || c.userBrief) parts.push((input.userBrief ?? c.userBrief) as string);
    if (c.releaseContext?.title) parts.push(`Release: ${c.releaseContext.title}${c.releaseContext.releaseType ? ` (${c.releaseContext.releaseType})` : ""}`);
    if (c.trackContext?.title) parts.push(`Track: ${c.trackContext.title}${c.trackContext.artist ? ` — ${c.trackContext.artist}` : ""}`);
    if (c.trackContext?.moods?.length) parts.push(`Moods: ${c.trackContext.moods.join(", ")}`);
    if (c.trackContext?.semanticTags?.length) parts.push(`Semantic: ${c.trackContext.semanticTags.join(", ")}`);
    if (c.setContext?.energyArcSummary) parts.push(`Energy arc: ${c.setContext.energyArcSummary}`);
    if (c.setContext?.emotionalArcSummary) parts.push(`Emotional arc: ${c.setContext.emotionalArcSummary}`);
    if (c.brandWorldContext) {
      const b = c.brandWorldContext;
      if (b.visualTone.length) parts.push(`Visual tone: ${b.visualTone.join(", ")}`);
      if (b.motifs.length) parts.push(`Motifs: ${b.motifs.join(", ")}`);
      if (b.colors.length) parts.push(`Palette: ${b.colors.join(", ")}`);
    }
    if (c.stemContext?.energyDrivers?.length) parts.push(`Driven by: ${c.stemContext.energyDrivers.join(", ")}`);
    // Lyrics: only key phrases, never full text — even if the allowedMode says full_private_only,
    // the bundle carries key phrases only for provider payloads (see createProviderPayload).
    if (c.lyricContext?.keyPhrases?.length && c.lyricContext.allowedMode !== "none") {
      parts.push(`Key phrases: ${c.lyricContext.keyPhrases.slice(0, 4).join(" / ")}`);
    }

    const positivePrompt = parts.filter(Boolean).join(" • ");

    // Negative prompt / forbidden elements: safety floor + brand-world restricted words.
    const forbidden = new Set<string>(DEFAULT_FORBIDDEN);
    for (const r of c.brandWorldContext?.restrictedElements ?? []) forbidden.add(r);
    const negativePrompt = [...forbidden].join(", ");

    // Style rules: brand world usage rules + implicit prohibitions.
    const styleRules = [...(c.brandWorldContext?.usageRules ?? [])];
    if (c.brandWorldContext) styleRules.push("Respect brand world palette and motif language.");
    if (c.lyricContext?.allowedMode === "key_phrases_only") styleRules.push("Do not use full lyric text; key phrases only.");
    if (c.lyricContext?.allowedMode === "none") styleRules.push("Do not reference lyric text.");

    const sourceRefs: Array<{ type: string; id: string }> = [];
    if (c.trackContext) sourceRefs.push({ type: "track", id: c.trackContext.trackId });
    if (c.setContext) sourceRefs.push({ type: "set", id: c.setContext.setId });
    if (c.releaseContext) sourceRefs.push({ type: "release", id: c.releaseContext.releaseId });
    if (c.brandWorldContext) sourceRefs.push({ type: "brand_world", id: c.brandWorldContext.brandWorldId });

    return {
      id: input.id ?? `prb_${c.projectId}_${fnv1a(stableStringify({ ctx: c.id, at: input.createdAt ?? AT }))}`,
      schemaVersion: AI_PROMPT_SCHEMA_VERSION,
      projectId: c.projectId, useCase: c.useCase,
      promptVersion: 1,
      contextId: c.id,
      positivePrompt, negativePrompt, styleRules,
      forbiddenElements: [...forbidden],
      providerHints: {},
      safetyWarnings: safety, rightsWarnings: rights,
      createdAt: input.createdAt ?? AT, createdBy: input.createdBy,
      sourceRefs,
    };
  }

  /**
   * Apply a patch to an existing prompt bundle, producing a new revision with an incremented version and diff summary.
   * @param input - the existing bundle, the patch, and the reason for the change.
   * @returns the new prompt revision, containing the updated bundle.
   */
  revisePromptBundle(input: RevisePromptBundleInput): DJSEMAIPromptRevision {
    const prev = input.existing;
    const patch = input.patch ?? {};
    const forbiddenChanged = patch.forbiddenElements ?? prev.forbiddenElements;
    // Compute a small diff summary for the human record.
    const diff: string[] = [];
    if (patch.positivePrompt !== undefined && patch.positivePrompt !== prev.positivePrompt) diff.push("positivePrompt changed");
    if (patch.negativePrompt !== undefined && patch.negativePrompt !== prev.negativePrompt) diff.push("negativePrompt changed");
    if (patch.styleRules) diff.push(`styleRules: ${patch.styleRules.length - prev.styleRules.length >= 0 ? "+" : ""}${patch.styleRules.length - prev.styleRules.length}`);
    if (patch.forbiddenElements) diff.push(`forbiddenElements: ${patch.forbiddenElements.length - prev.forbiddenElements.length >= 0 ? "+" : ""}${patch.forbiddenElements.length - prev.forbiddenElements.length}`);
    if (patch.providerHints) diff.push("providerHints changed");

    const next: DJSEMAIPromptBundle = {
      ...prev,
      positivePrompt: patch.positivePrompt ?? prev.positivePrompt,
      negativePrompt: patch.negativePrompt ?? prev.negativePrompt,
      styleRules: patch.styleRules ?? prev.styleRules,
      forbiddenElements: forbiddenChanged,
      providerHints: patch.providerHints ?? prev.providerHints,
      promptVersion: prev.promptVersion + 1,
      createdAt: input.changedAt ?? prev.createdAt,
      createdBy: input.changedBy ?? prev.createdBy,
    };

    return {
      id: `pbr_${prev.id}_${next.promptVersion}`,
      promptBundleId: prev.id, revisionNumber: next.promptVersion,
      changedAt: input.changedAt ?? AT, changedBy: input.changedBy,
      reason: input.reason, diffSummary: diff, bundle: next,
    };
  }

  /**
   * Build a provider-specific payload from a bundle, stripping any secret-looking keys from parameters/providerHints.
   * @param input - the bundle, provider name, and optional provider-specific parameters.
   * @returns the provider payload plus a list of redacted keys.
   */
  createProviderPayload(input: CreateProviderPromptPayloadInput): DJSEMAIProviderPromptPayload {
    const redactions: string[] = [];
    const clean: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input.parameters ?? {})) {
      if (FORBIDDEN_KEYS.has(k)) { redactions.push(k); continue; }
      clean[k] = v;
    }
    // Also strip forbidden-key smuggle in providerHints (defense in depth).
    for (const [k, v] of Object.entries(input.bundle.providerHints ?? {})) {
      if (FORBIDDEN_KEYS.has(k)) { redactions.push(`providerHints.${k}`); continue; }
      if (!(k in clean)) clean[k] = v;
    }
    return {
      bundleId: input.bundle.id, provider: input.provider,
      positivePrompt: input.bundle.positivePrompt, negativePrompt: input.bundle.negativePrompt,
      parameters: clean, redactions,
    };
  }

  /**
   * Validate a prompt bundle's required fields and check for forbidden (secret-like) hint keys.
   * @param bundle - the bundle to validate.
   * @returns validation result.
   */
  validatePromptBundle(bundle: DJSEMAIPromptBundle): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!bundle.positivePrompt.trim()) issues.push({ path: "$.positivePrompt", message: "positivePrompt required" });
    if (bundle.promptVersion < 1) issues.push({ path: "$.promptVersion", message: "promptVersion must be >= 1" });
    for (const k of Object.keys(bundle.providerHints ?? {})) {
      if (FORBIDDEN_KEYS.has(k)) issues.push({ path: `$.providerHints.${k}`, message: "forbidden key (looks like a secret)" });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "ai_prompt_bundle");
  }
}

/** Records and diffs prompt bundle changes for provenance/audit purposes. */
export class AICreativePromptAuditService {
  /**
   * Compute which top-level fields changed between two prompt bundle versions, and the forbidden-elements delta.
   * @param input - the two bundle versions to compare.
   * @returns added/removed forbidden elements and the list of changed field names.
   */
  diffPromptBundles(input: DiffPromptBundlesInput): DJSEMPromptDiffResult {
    const changed: string[] = [];
    const cmp = (k: keyof DJSEMAIPromptBundle) => {
      const av = stableStringify(input.a[k]); const bv = stableStringify(input.b[k]);
      if (av !== bv) changed.push(String(k));
    };
    (["positivePrompt", "negativePrompt", "styleRules", "forbiddenElements", "providerHints", "useCase"] as const).forEach(cmp);
    // Added / removed forbidden elements — the most interesting audit slice.
    const aSet = new Set(input.a.forbiddenElements); const bSet = new Set(input.b.forbiddenElements);
    const added = [...bSet].filter((x) => !aSet.has(x));
    const removed = [...aSet].filter((x) => !bSet.has(x));
    return { added, removed, changed };
  }

  /**
   * Create an audit-trail record for an action taken against a prompt bundle.
   * @param input - the bundle id, action name, and arbitrary details to record.
   * @returns the created audit record.
   */
  createAuditRecord(input: CreatePromptAuditInput): DJSEMPromptAuditRecord {
    return {
      id: `pra_${input.bundleId}_${fnv1a(stableStringify({ a: input.action, at: input.recordedAt ?? AT }))}`,
      bundleId: input.bundleId, recordedAt: input.recordedAt ?? AT, recordedBy: input.recordedBy,
      action: input.action, details: input.details,
    };
  }
}
